import "flatpickr/dist/flatpickr.min.css";
import "flatpickr/dist/themes/material_blue.css";
import "./DatePicker.css";

import type flatpickr from "flatpickr";
import React from "react";
import Flatpickr from "react-flatpickr";

type BaseDatePickerProps = {
    className?: string;
    placeholder?: string;
    clearable?: boolean;
};

type SingleDatePickerProps = BaseDatePickerProps & {
    mode?: "single";
    value?: Date;
    onChange: (date: Date | undefined) => void;
};

type RangeDatePickerProps = BaseDatePickerProps & {
    mode: "range";
    value?: [Date | undefined, Date | undefined];
    onChange: (dates: [Date | undefined, Date | undefined]) => void;
};

export type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps;

export default function DatePicker(props: DatePickerProps) {
    const {
        mode = "single",
        placeholder = "Select date",
        className = "",
        clearable = true,
    } = props;

    const options = React.useMemo(
        () => ({
            mode,
            dateFormat: "Y-m-d",
        }),
        [mode],
    );

    const fpInstance = React.useRef<flatpickr.Instance | null>(null);

    const rangeStart = props.mode === "range" ? props.value?.[0] : undefined;
    const rangeEnd = props.mode === "range" ? props.value?.[1] : undefined;
    const singleValue = props.mode === "range" ? undefined : props.value;

    const handleChange = (dates: Date[]) => {
        if (props.mode === "range") {
            if (dates.length === 0) {
                props.onChange([undefined, undefined]);
            } else if (dates.length === 2) {
                props.onChange([dates[0], dates[1]]);
            }

            return;
        }

        props.onChange(dates[0]);
    };

    const syncValue = React.useCallback(
        (instance: flatpickr.Instance) => {
            if (mode === "range") {
                const dates = [rangeStart, rangeEnd].filter(
                    (date): date is Date => date instanceof Date,
                );

                if (dates.length > 0) {
                    instance.setDate(dates, false);
                } else {
                    instance.clear(false);
                }

                return;
            }

            if (singleValue instanceof Date) {
                instance.setDate(singleValue, false);
            } else {
                instance.clear(false);
            }
        },
        [mode, rangeStart, rangeEnd, singleValue],
    );

    React.useEffect(() => {
        const instance = fpInstance.current;
        if (!instance) return;

        syncValue(instance);
    }, [syncValue]);

    const hasValue =
        props.mode === "range"
            ? Boolean(rangeStart || rangeEnd)
            : singleValue instanceof Date;

    const handleClear = () => {
        fpInstance.current?.clear(false);

        if (props.mode === "range") {
            props.onChange([undefined, undefined]);
        } else {
            props.onChange(undefined);
        }
    };

    return (
        <div className={`position-relative ${className}`}>
            <Flatpickr
                onChange={handleChange}
                options={options}
                className="date-picker-input p-1 ps-2"
                placeholder={placeholder}
                onReady={(_, __, instance) => {
                    fpInstance.current = instance;
                    syncValue(instance);
                }}
            />

            {clearable && hasValue && (
                <button
                    type="button"
                    className="btn position-absolute top-50 end-0 translate-middle-y me-2 p-1 text-muted"
                    onClick={handleClear}
                    aria-label="Clear date"
                >
                    ✕
                </button>
            )}
        </div>
    );
}
